// Assignability for a slot — the single source of truth used by the manual
// dropdown, the visual board, the suggester, and the coverage summary.
//
// Owner ruling 2026-07-17: a guide who marked NEITHER ✨ nor ❌ on a slot is
// still assignable. ✨ means preferred (assign these first); ❌ means never
// assign via the hub. Benched (inactive) guides and guides not qualified for
// the slot's tour are not candidates at all.
//
// Tiers:
//   preferred — marked ✨ on the row (and not also ❌; ❌ wins a conflict)
//   open      — no marker, active, qualified for the tour (or in the market
//               when the tour can't be resolved to a product)
//   unavailable — marked ❌ (listed for visibility, never assignable)

import type { GuideRef, PlanSlot } from "@/lib/load";
import { guideRefById } from "@/lib/load";
import { GUIDES } from "@/config/guides";
import { PRODUCTS } from "@/config/products";
import { REGION_CODE } from "@/lib/markets";

export type AvailTier = "preferred" | "open" | "unavailable";

export interface SlotCandidates {
  preferred: GuideRef[];
  open: GuideRef[];
  unavailable: GuideRef[];
}

// Notion TOUR select label -> Peek productId. The live TOUR select carries
// short codes that don't all match config `notionTour` ("TUC Haunted History",
// "FLG Mountain Madness" — verified against the 2026 Bookable Tours export),
// so each product also registers its known aliases. Lookup is case-insensitive.
const TOUR_ALIASES: Record<string, string[]> = {
  "80a1896b-62c1-4d84-a6f4-c8ddf65ff074": ["Mural Tour of Historic Downtown Flagstaff"],
  "0ced34cb-0eb9-4f59-bc48-f5cb72b72187": [
    "FLG Mountain Madness", // live TOUR select value
    "Mountain Town of Madness Adults-Only Tour",
  ],
  "b419e3fd-85f0-416d-9018-6a6f5d0426fd": ["Route 66 Centennial Walking Tour Flagstaff"],
  "1898909d-ba15-436a-8c4f-452945d70f9f": ["Downtown Flagstaff Haunted History Tour"],
  "8d66ea83-c5af-42ea-90ec-9ba78f05d7f2": [
    "PRSCT Haunted History", // legacy prefix family
    "Downtown Prescott Haunted History Tour",
  ],
  "0bc9822a-4109-461c-89bf-41ac494c61f7": ["PRSCT History", "Prescott History Tour"],
  "2dae269d-e8a0-4afb-a926-0bae94468d37": ["TUC Mural"], // legacy Tucson prefix
  "7c6e9626-35fd-4e72-887a-f8fb29a04cb8": [
    "TUC Haunted History", // live TOUR select value
    "Downtown Tucson Haunted History Tour",
  ],
};

const labelToProductId: Record<string, string> = {};
for (const p of PRODUCTS) {
  const labels = [p.short, p.notionTour ?? "", ...(TOUR_ALIASES[p.id] ?? [])];
  for (const label of labels) {
    const key = label.trim().toLowerCase();
    if (key) labelToProductId[key] = p.id;
  }
}

/** Resolve a Notion TOUR label to a Peek productId, or null if unknown. */
export function productIdForTour(tour: string): string | null {
  return labelToProductId[(tour || "").trim().toLowerCase()] ?? null;
}

/**
 * All candidates for a slot, bucketed by tier. `preferred` is taken from the
 * row's ✨ checks verbatim (minus anyone who also checked ❌), so a ✨ guide is
 * never dropped even if the roster/product mapping disagrees. `open` comes from
 * the static roster: active guides qualified for the tour's product, falling
 * back to the slot's market when the tour label is unknown.
 */
export function candidatesFor(slot: PlanSlot): SlotCandidates {
  const noIds = new Set(slot.unavailable.map((g) => g.id));
  const preferred = slot.available.filter((g) => !noIds.has(g.id));
  const taken = new Set(preferred.map((g) => g.id));

  const productId = productIdForTour(slot.tour);
  const open: GuideRef[] = [];
  for (const g of GUIDES) {
    if (g.inactive || taken.has(g.id) || noIds.has(g.id)) continue;
    const qualified = productId
      ? g.productIds.includes(productId)
      : slot.market != null && REGION_CODE[g.region] === slot.market;
    if (!qualified) continue;
    const ref = guideRefById(g.id);
    if (ref) open.push(ref);
  }
  open.sort((a, b) => a.peekName.localeCompare(b.peekName));

  return { preferred, open, unavailable: slot.unavailable };
}

/** Tier of one guide on one slot (for the board's cell shading). */
export function tierOf(slot: PlanSlot, guideId: string): AvailTier | null {
  if (slot.unavailable.some((g) => g.id === guideId)) return "unavailable";
  if (slot.available.some((g) => g.id === guideId)) return "preferred";
  const c = candidatesFor(slot);
  return c.open.some((g) => g.id === guideId) ? "open" : null;
}
